import { Link } from "react-router-dom";
import type { Media } from "@zivin-labs/site-content";
import { Figure } from "../Figure";
import { ROUTES } from "../../lib/routes";
import {
  AUDITORIUM_PHOTOS,
  NCC_PHOTOS,
  SAC_PHOTOS,
  SPORTS_PHOTOS,
} from "../../lib/campusLifeMedia";

/** One ingested photo out of a pool, by its key's last segment. */
function shot(pool: Media[], slug: string): Media | undefined {
  return pool.find((m) => m.url.endsWith(`/${slug}`));
}

/**
 * Four cards mirroring /campus-life's real pillars, each deep-linking to its
 * section there. Copy is drawn from the sourced data behind that page — the
 * prototype's cards claimed an "Anubhava cultural fest", "14 active clubs" and
 * inter-collegiate titles "in 6 disciplines in 2025", none of it published
 * anywhere.
 *
 * Photos are the institute's own, chosen by previewing the 4:5 crop: the SAC
 * collage centres on its classical dance troupe, the kabaddi team crops to the
 * players in kit, and auditorium-17 is the centre's facade lit for an evening
 * event — distinct from the daytime exterior /campus-life leads with. (Tried
 * and dropped: the library exterior loses its signage in the crop, sports-04
 * is a newspaper scan, auditorium-04 is the podium.)
 */
const CARDS = [
  {
    title: "Student Council",
    desc: "The Students' Academic Council has run its own affairs since 1984 — the Davana festival, Ethnic Day, elections and the multi gym, all student-managed.",
    media: shot(SAC_PHOTOS, "collage3.png"),
    img: "SAC · DAVANA FESTIVAL",
    to: `${ROUTES.campusLife}#council`,
  },
  {
    title: "Service Wings",
    desc: "NSS, NCC and the Youth Red Cross Wing — three service wings, each led by a faculty programme officer, from blood-donation camps to cadet training.",
    media: shot(NCC_PHOTOS, "ncc-01.jpg"),
    img: "NCC · CADET",
    to: `${ROUTES.campusLife}#service`,
  },
  {
    title: "Sports",
    desc: "Teams at VTU inter-collegiate level — athletics, badminton, basketball, kabaddi, throwball and football, with an annual sports report every year.",
    media: shot(SPORTS_PHOTOS, "college-kabaddi-team.jpg"),
    img: "SPORTS · KABADDI TEAM",
    to: `${ROUTES.campusLife}#sport`,
  },
  {
    title: "Cultural Centre",
    desc: "The 700-seat S. S. Mallikarjun Cultural Centre — a ₹28-crore auditorium with acoustics from Germany and projection from Canada.",
    media: shot(AUDITORIUM_PHOTOS, "auditorium-17.jpg"),
    img: "SSM CULTURAL CENTRE · EVENING",
    to: `${ROUTES.campusLife}#cultural-centre`,
  },
];

export function CampusLife() {
  return (
    <section className="section campus-section">
      <div className="container">
        <div className="section-head">
          <div>
            <div className="kicker">Beyond the classroom</div>
            <h2>Campus life</h2>
          </div>
          <Link className="btn-link" to={ROUTES.campusLife}>
            Explore campus <span className="arr">→</span>
          </Link>
        </div>
        <div className="campus-grid">
          {CARDS.map((c) => (
            <Link key={c.title} to={c.to} className="campus-card reveal">
              <Figure
                media={c.media}
                fallbackLabel={c.img}
                aspect="4/5"
                sizes="(max-width: 640px) 100vw, (max-width: 1000px) 48vw, 24vw"
              />
              <div className="campus-body">
                <h4>{c.title}</h4>
                <p>{c.desc}</p>
                <span className="campus-link">Know more →</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
